import domReady from '@wordpress/dom-ready';

import {
  TOOLTIP_ATTRIBUTE_NAME,
  TOOLTIP_KEY,
  TOOLTIP_JS_ENABLED_BODY_CLASS_NAME,
  TOOLTIP_PORTAL_ARROW_CLASS_NAME,
  TOOLTIP_PORTAL_CLASS_NAME,
  TOOLTIP_PORTAL_CONTENT_CLASS_NAME,
  TOOLTIP_PORTAL_TEXT_CLASS_NAME,
  TOOLTIP_TRIGGER_CLASS_NAME,
  TOOLTIP_VISIBLE_CLASS_NAME,
} from './constants';
import './_tooltip.scss';

const TOOLTIP_VERTICAL_GAP = 8;
const TOOLTIP_EDGE_PADDING = 10;
const TOOLTIP_HIDE_DELAY = 100;
const TOOLTIP_RESIZE_DELAY = 100;

const TOOLTIP_TRIGGER_SELECTOR = `.${TOOLTIP_TRIGGER_CLASS_NAME}[${TOOLTIP_ATTRIBUTE_NAME}]`;

let tooltipContainer = null;
let tooltipElement = null;
let tooltipTextElement = null;
let tooltipArrowElement = null;
let currentTrigger = null;
let hideTimeoutId = null;
let repositionFrameId = null;
let resizeTimeoutId = null;
let isInitialized = false;

function getTooltipTrigger(node) {
  if (!node || typeof node.closest !== 'function') {
    return null;
  }

  return node.closest(TOOLTIP_TRIGGER_SELECTOR);
}

function clearHideTimeout() {
  if (hideTimeoutId) {
    clearTimeout(hideTimeoutId);
    hideTimeoutId = null;
  }
}

function ensureTooltipElements() {
  if (tooltipContainer) {
    return;
  }

  tooltipContainer = document.createElement('div');
  tooltipContainer.className = TOOLTIP_PORTAL_CLASS_NAME;
  tooltipContainer.setAttribute('aria-hidden', 'true');

  tooltipElement = document.createElement('div');
  tooltipElement.className = TOOLTIP_PORTAL_CONTENT_CLASS_NAME;

  tooltipTextElement = document.createElement('div');
  tooltipTextElement.className = TOOLTIP_PORTAL_TEXT_CLASS_NAME;

  tooltipArrowElement = document.createElement('div');
  tooltipArrowElement.className = TOOLTIP_PORTAL_ARROW_CLASS_NAME;

  tooltipElement.append(tooltipTextElement, tooltipArrowElement);
  tooltipContainer.appendChild(tooltipElement);
  document.body.appendChild(tooltipContainer);
}

function hideTooltip() {
  if (!tooltipElement) {
    return;
  }

  tooltipElement.classList.remove(TOOLTIP_VISIBLE_CLASS_NAME);
  currentTrigger = null;
}

function positionTooltip(trigger) {
  if (!tooltipElement || !tooltipArrowElement || !trigger.isConnected) {
    hideTooltip();
    return;
  }

  const triggerRect = trigger.getBoundingClientRect();

  if (
    triggerRect.width === 0 ||
    triggerRect.height === 0 ||
    triggerRect.top < 0 ||
    triggerRect.bottom > window.innerHeight ||
    triggerRect.left < -triggerRect.width ||
    triggerRect.right > window.innerWidth + triggerRect.width
  ) {
    hideTooltip();
    return;
  }

  const tooltipRect = tooltipElement.getBoundingClientRect();
  const left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
  const top = triggerRect.top - tooltipRect.height - TOOLTIP_VERTICAL_GAP;

  const maxLeft = window.innerWidth - tooltipRect.width - TOOLTIP_EDGE_PADDING;
  const finalLeft = Math.max(TOOLTIP_EDGE_PADDING, Math.min(left, maxLeft));

  tooltipElement.style.left = `${finalLeft}px`;
  tooltipElement.style.top = `${top}px`;

  const arrowLeft = triggerRect.left + triggerRect.width / 2 - finalLeft;
  tooltipArrowElement.style.left = `${arrowLeft}px`;

  if (!tooltipElement.classList.contains(TOOLTIP_VISIBLE_CLASS_NAME)) {
    requestAnimationFrame(() => {
      if (currentTrigger === trigger) {
        tooltipElement.classList.add(TOOLTIP_VISIBLE_CLASS_NAME);
      }
    });
  }
}

function showTooltip(trigger) {
  const content = trigger.dataset[TOOLTIP_KEY];

  if (!content || !content.trim()) {
    hideTooltip();
    return;
  }

  ensureTooltipElements();

  tooltipTextElement.textContent = content;
  currentTrigger = trigger;

  positionTooltip(trigger);
}

function scheduleReposition() {
  if (repositionFrameId || !currentTrigger) {
    return;
  }

  repositionFrameId = window.requestAnimationFrame(() => {
    repositionFrameId = null;
    if (currentTrigger) {
      positionTooltip(currentTrigger);
    }
  });
}

function handleMouseOver(event) {
  const trigger = getTooltipTrigger(event.target);

  if (!trigger) {
    return;
  }

  if (event.relatedTarget && trigger.contains(event.relatedTarget)) {
    return;
  }

  clearHideTimeout();

  if (currentTrigger === trigger) {
    return;
  }

  showTooltip(trigger);
}

function handleMouseOut(event) {
  const trigger = getTooltipTrigger(event.target);

  if (!trigger || trigger !== currentTrigger) {
    return;
  }

  if (event.relatedTarget && trigger.contains(event.relatedTarget)) {
    return;
  }

  clearHideTimeout();
  hideTimeoutId = window.setTimeout(() => {
    hideTimeoutId = null;
    hideTooltip();
  }, TOOLTIP_HIDE_DELAY);
}

function handleResize() {
  clearTimeout(resizeTimeoutId);
  resizeTimeoutId = window.setTimeout(() => {
    resizeTimeoutId = null;
    scheduleReposition();
  }, TOOLTIP_RESIZE_DELAY);
}

function initializeTooltips() {
  if (isInitialized || !document.body) {
    return;
  }

  isInitialized = true;
  document.body.classList.add(TOOLTIP_JS_ENABLED_BODY_CLASS_NAME);

  document.addEventListener('mouseover', handleMouseOver);
  document.addEventListener('mouseout', handleMouseOut);
  window.addEventListener('scroll', scheduleReposition, true);
  window.addEventListener('resize', handleResize);
}

domReady(initializeTooltips);
